/**
 * Contact Deduplication Page
 * Review possible duplicate contacts.
 *
 * Merging from this page was removed 2026-10-01 (owner): the old client-side merge wrote a
 * `contacts.metadata` column that does not exist and a `status = 'merged'` the database forbids, so it
 * failed on its first write every time. Merges run only through the service-role merge functions.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  findDuplicateContacts,
  getDuplicateStats,
  ignoreDuplicateGroup,
  isDuplicateGroupIgnored,
  clearIgnoredDuplicates,
  type DuplicateGroup,
} from "../services/deduplication.service";
import {
  UserGroupIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  EyeSlashIcon,
  ArrowsPointingInIcon,
  EnvelopeIcon,
  PhoneIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { useToast } from "../components/ui/Toast";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Button from "../components/common/Button";

export default function Deduplication() {
  const { showToast } = useToast();

  const [showIgnored, setShowIgnored] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<DuplicateGroup | null>(
    null,
  );

  // Fetch duplicate stats
  const { data: stats } = useQuery({
    queryKey: ["duplicate-stats"],
    queryFn: getDuplicateStats,
  });

  // Fetch duplicate groups
  const {
    data: duplicates = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["duplicate-contacts"],
    queryFn: findDuplicateContacts,
  });

  // Filter out ignored groups
  const visibleDuplicates = showIgnored
    ? duplicates
    : duplicates.filter((d) => !isDuplicateGroupIgnored(d.id));

  const handleIgnoreGroup = (group: DuplicateGroup) => {
    ignoreDuplicateGroup(
      group.id,
      group.contacts.map((c) => c.id),
    );
    refetch();
    showToast("info", "Duplicate group ignored");
  };

  const getMatchIcon = (matchType: string) => {
    switch (matchType) {
      case "email":
        return <EnvelopeIcon className="h-4 w-4" />;
      case "phone":
        return <PhoneIcon className="h-4 w-4" />;
      case "name":
        return <UserIcon className="h-4 w-4" />;
      default:
        return <UserGroupIcon className="h-4 w-4" />;
    }
  };

  const getMatchLabel = (matchType: string) => {
    switch (matchType) {
      case "email":
        return "Email Match";
      case "phone":
        return "Phone Match";
      case "name":
        return "Name Match";
      default:
        return "Match";
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) {
      return "-";
    }
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Contact Deduplication
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Review possible duplicate contact records
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={showIgnored}
              onChange={(e) => setShowIgnored(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Show ignored
          </label>
          <button
            onClick={() => {
              clearIgnoredDuplicates();
              refetch();
            }}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Reset ignored
          </button>
          <Button
            variant="secondary"
            icon={ArrowPathIcon}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <UserGroupIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {stats.total_contacts.toLocaleString()}
                </div>
                <div className="text-sm text-gray-500">Total Contacts</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-50 rounded-lg">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-yellow-600">
                  {stats.duplicate_groups}
                </div>
                <div className="text-sm text-gray-500">Duplicate Groups</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-50 rounded-lg">
                <ArrowsPointingInIcon className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600">
                  {stats.duplicate_contacts}
                </div>
                <div className="text-sm text-gray-500">Duplicate Records</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Duplicate Groups List */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h3 className="font-medium text-gray-900">Potential Duplicates</h3>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : visibleDuplicates.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-gray-900 mb-1">
                No Duplicates Found
              </h4>
              <p className="text-gray-500">
                Your contact database looks clean!
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
              {visibleDuplicates.map((group) => (
                <div
                  key={group.id}
                  className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedGroup?.id === group.id
                      ? "bg-blue-50 border-l-4 border-blue-500"
                      : ""
                  }`}
                  onClick={() => {
                    setSelectedGroup(group);
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                            group.match_type === "email"
                              ? "bg-blue-100 text-blue-700"
                              : group.match_type === "phone"
                                ? "bg-purple-100 text-purple-700"
                                : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {getMatchIcon(group.match_type)}
                          {getMatchLabel(group.match_type)}
                        </span>
                        <span className="text-xs text-gray-500">
                          {group.confidence}% confidence
                        </span>
                      </div>
                      <div className="mt-2 space-y-1">
                        {group.contacts.slice(0, 3).map((contact) => (
                          <div key={contact.id} className="text-sm">
                            <span className="font-medium text-gray-900">
                              {contact.first_name} {contact.last_name}
                            </span>
                            {contact.email && (
                              <span className="text-gray-500 ml-2">
                                {contact.email}
                              </span>
                            )}
                          </div>
                        ))}
                        {group.contacts.length > 3 && (
                          <div className="text-xs text-gray-500">
                            +{group.contacts.length - 3} more
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">
                        {group.contacts.length}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleIgnoreGroup(group);
                        }}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                        title="Ignore this group"
                      >
                        <EyeSlashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Compare Panel (read-only) */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h3 className="font-medium text-gray-900">Compare Contacts</h3>
          </div>

          {!selectedGroup ? (
            <div className="text-center py-12 text-gray-500">
              <ArrowsPointingInIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Select a group to compare its contacts</p>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {selectedGroup.contacts.map((contact) => (
                  <div
                    key={contact.id}
                    className="p-4 rounded-lg border-2 border-gray-200"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">
                            {contact.first_name} {contact.last_name}
                          </span>
                        </div>
                        <div className="mt-2 text-sm text-gray-600 space-y-1">
                          {contact.email && (
                            <div className="flex items-center gap-2">
                              <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                              {contact.email}
                            </div>
                          )}
                          {contact.phone_number && (
                            <div className="flex items-center gap-2">
                              <PhoneIcon className="h-4 w-4 text-gray-400" />
                              {contact.phone_number}
                            </div>
                          )}
                          {(contact.city || contact.state) && (
                            <div className="text-gray-500">
                              {[contact.city, contact.state]
                                .filter(Boolean)
                                .join(", ")}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right text-xs text-gray-500">
                        <div>Created: {formatDate(contact.created_at)}</div>
                        <div>Updated: {formatDate(contact.updated_at)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end pt-4 border-t border-gray-200">
                <button
                  onClick={() => setSelectedGroup(null)}
                  className="text-sm text-gray-600 hover:text-gray-900"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-800 mb-2">
          Merging is turned off on this page
        </h4>
        <p className="text-sm text-blue-700">
          Use this list to review possible duplicates and hide groups that
          aren&apos;t real duplicates. If two records really are the same person
          and need to be combined, ask the CRM team.
        </p>
      </div>
    </div>
  );
}
